/**
 * Typed fetch wrappers over the wire contract in src/domain/api.ts.
 * Every import from '@shared' is type-only — nothing from the server bundles.
 */
import type {
  AuthStatusResponse,
  ChatEvent,
  ChatRequest,
  ContentLibraryResponse,
  CreditAdminResponse,
  CreditRulesFile,
  CreditsResponse,
  FeatureFlagsResponse,
  GeocodeResponse,
  ImageConfigResponse,
  MapConfigResponse,
  MediaResponse,
  MutateRequest,
  ProposalAccessResponse,
  ProposalBuildState,
  ProposalListResponse,
  ProposalResponse,
  ShareResponse,
  SharedProposalResponse,
  StockSearchResponse,
  VersionListResponse,
  ViewerListResponse,
  ZipBoundariesResponse,
} from '@shared/api';
import type { Mutation } from '@shared/types';

/**
 * Mirrors src/build-engines/types.ts's BuildEngine. `@shared` only resolves
 * to src/domain (see web/vite.config.ts) so this is a plain literal union
 * rather than an import — it is exactly three values and changes only through
 * the same Build Engine Selector v1 spec that would touch the server type.
 */
export type BuildEngine = 'classic' | 'studio' | 'forge';

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * Fired on ANY 401 from `/api` — a session that was never valid, or one that
 * expired/was signed out elsewhere mid-use. `AuthGate` (App.tsx) listens for
 * this and switches to the sign-in screen immediately, rather than waiting
 * for the next `/api/auth/status` poll to notice.
 */
export const UNAUTHORIZED_EVENT = 'viq:unauthorized';

function reportUnauthorized(): void {
  window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
}

/**
 * The share token `/share/:token` is currently showing, if any — set by
 * SharedView on mount/unmount. Every `request()` call below attaches it as
 * `X-Share-Token`, the credential GET /api/proposals/:id and its media
 * accept in place of a session (server/index.ts's /api gate). SharedView is
 * the only thing that ever sets this, and it's never mounted at the same
 * time as the signed-in workspace, so this can't leak a share token onto an
 * unrelated authenticated request.
 */
let activeShareToken: string | null = null;

export function setShareToken(token: string | null): void {
  activeShareToken = token;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init?.body ? { 'Content-Type': 'application/json' } : null),
        ...(activeShareToken ? { 'X-Share-Token': activeShareToken } : null),
        ...init?.headers,
      },
    });
  } catch (cause) {
    throw new ApiError(0, `Could not reach the server (${String(cause)}).`);
  }
  if (res.status === 401) reportUnauthorized();
  const text = await res.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }
  if (!res.ok) {
    const message =
      payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Request failed (${res.status}).`;
    throw new ApiError(res.status, message);
  }
  return payload as T;
}

// ---------------------------------------------------------------------------
// Proposals
// ---------------------------------------------------------------------------

export function listProposals(): Promise<ProposalListResponse> {
  return request<ProposalListResponse>('/api/proposals');
}

export function createProposal(
  initialBrief?: string,
  buildEngine?: BuildEngine,
): Promise<ProposalResponse> {
  return request<ProposalResponse>('/api/proposals', {
    method: 'POST',
    body: JSON.stringify({
      ...(initialBrief ? { initialBrief } : null),
      ...(buildEngine ? { buildEngine } : null),
    }),
  });
}

/** Build Engine Selector v1 (spec §35, §66): rebind the NEXT build to a different engine. */
export function setBuildEngine(id: string, buildEngine: BuildEngine): Promise<ProposalResponse> {
  return request<ProposalResponse>(`/api/proposals/${encodeURIComponent(id)}/build-engine`, {
    method: 'POST',
    body: JSON.stringify({ buildEngine }),
  });
}

export function getProposal(id: string, version?: number): Promise<ProposalResponse> {
  const q = version === undefined ? '' : `?version=${encodeURIComponent(String(version))}`;
  return request<ProposalResponse>(`/api/proposals/${encodeURIComponent(id)}${q}`);
}

export function getProposalBuild(id: string): Promise<ProposalBuildState> {
  return request<ProposalBuildState>(`/api/proposals/${encodeURIComponent(id)}/build`);
}

export function approveProposalBuild(id: string): Promise<ProposalBuildState> {
  return request<ProposalBuildState>(`/api/proposals/${encodeURIComponent(id)}/build/approve`, { method: 'POST' });
}

export function listVersions(id: string): Promise<VersionListResponse> {
  return request<VersionListResponse>(`/api/proposals/${encodeURIComponent(id)}/versions`);
}

/** The only way plan or document state ever moves from the client. */
export function postMutations(
  id: string,
  mutations: Mutation[],
  changeSummary: string,
): Promise<ProposalResponse> {
  const body: MutateRequest = { mutations, changeSummary };
  return request<ProposalResponse>(`/api/proposals/${encodeURIComponent(id)}/mutations`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function undo(id: string): Promise<ProposalResponse> {
  return request<ProposalResponse>(`/api/proposals/${encodeURIComponent(id)}/undo`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * Aborts the running chat turn after its current tool (docs/composer-cards.md
 * 10h). The SSE stream stays open and still delivers `state` + `done{stopped:
 * true}` through the normal path — this call does not touch the stream.
 * 202 when a turn was running, 409 otherwise; both are fine to ignore.
 */
export function stopChat(id: string): Promise<void> {
  return request<void>(`/api/proposals/${encodeURIComponent(id)}/chat/stop`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * Reports that the rep skipped the current ask (docs/message-bar-states.md
 * 4, docs/composer-cards.md 4d) so the agent never sees that question as
 * still open. Fire-and-forget: nothing in the UI depends on the response,
 * and the route may not exist yet on every server (a 404 is fine to ignore).
 */
export function skipAsk(id: string): Promise<void> {
  return request<void>(`/api/proposals/${encodeURIComponent(id)}/chat/skip`, {
    method: 'POST',
    body: JSON.stringify({}),
  }).catch(() => undefined);
}

export function revert(id: string, version: number): Promise<ProposalResponse> {
  return request<ProposalResponse>(`/api/proposals/${encodeURIComponent(id)}/revert`, {
    method: 'POST',
    body: JSON.stringify({ version }),
  });
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

/** Downloads the server-rendered PDF snapshot of the current version. */
export async function exportPdf(id: string): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`/api/proposals/${encodeURIComponent(id)}/export.pdf`);
  if (!res.ok) {
    if (res.status === 401) reportUnauthorized();
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Export failed (${res.status})`);
  }
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  return { blob: await res.blob(), filename: match?.[1] ?? 'proposal.pdf' };
}

export function authStatus(): Promise<AuthStatusResponse> {
  return request<AuthStatusResponse>('/api/auth/status');
}

/**
 * The password is passed straight through to the local server and never kept,
 * cached or logged on this side.
 */
export function authLogin(username: string, password: string): Promise<AuthStatusResponse> {
  return request<AuthStatusResponse>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
}

export function authLogout(): Promise<AuthStatusResponse> {
  return request<AuthStatusResponse>('/api/auth/logout', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function authSelectOrganization(
  organizationId: string | null,
  cobrand: string | null = null,
): Promise<AuthStatusResponse> {
  return request<AuthStatusResponse>('/api/auth/organization', {
    method: 'POST',
    body: JSON.stringify({ organizationId, cobrand }),
  });
}

// ---------------------------------------------------------------------------
// Proposal credits (docs/limits.md)
// ---------------------------------------------------------------------------

export function getCredits(): Promise<CreditsResponse> {
  return request<CreditsResponse>('/api/credits');
}

/** The one credits admin only (src/server/credit-admin.ts); 403 for everyone else. */
export function getCreditAdmin(): Promise<CreditAdminResponse> {
  return request<CreditAdminResponse>('/api/admin/credits');
}

export function saveCreditRules(rules: CreditRulesFile): Promise<CreditAdminResponse> {
  return request<CreditAdminResponse>('/api/admin/credits/rules', { method: 'PUT', body: JSON.stringify(rules) });
}

/** The agency's VisitIQ name from its id; name is null when no org has that id. */
export function lookupCreditAgency(id: string): Promise<{ id: string; name: string | null }> {
  return request<{ id: string; name: string | null }>(`/api/admin/credits/agencies/${encodeURIComponent(id)}`);
}

export function refundCredit(proposalId: string, reason?: string): Promise<CreditAdminResponse> {
  return request<CreditAdminResponse>('/api/admin/credits/refund', {
    method: 'POST',
    body: JSON.stringify({ proposalId, ...(reason ? { reason } : null) }),
  });
}

// ---------------------------------------------------------------------------
// Feature flags
// ---------------------------------------------------------------------------

/** Build Engine Selector v1: which beta features are on (e.g. the Studio start-screen card). */
export function getFeatures(): Promise<FeatureFlagsResponse> {
  return request<FeatureFlagsResponse>('/api/features');
}

// ---------------------------------------------------------------------------
// Content library
// ---------------------------------------------------------------------------

export function getContentLibrary(): Promise<ContentLibraryResponse> {
  return request<ContentLibraryResponse>('/api/content-library');
}

// ---------------------------------------------------------------------------
// Maps & geocoding
// ---------------------------------------------------------------------------

export function getMapConfig(): Promise<MapConfigResponse> {
  return request<MapConfigResponse>('/api/map-config');
}

/** Server-side geocode of a human place label, e.g. "Queensbury, NY". */
export function getGeocode(label: string): Promise<GeocodeResponse> {
  return request<GeocodeResponse>(`/api/geocode?q=${encodeURIComponent(label)}`);
}

/**
 * ZCTA (ZIP code) polygons near a service-area query (design.md V8), server-
 * generalized to slide resolution and disk-cached per rounded query. The
 * caller is expected to keep its own per-query session cache (mirrors the
 * geocode cache pattern above) — this is never called per-render.
 */
export function getZipBoundaries(
  lat: number,
  lng: number,
  radiusMiles: number,
): Promise<ZipBoundariesResponse> {
  const params = new URLSearchParams({
    lat: String(lat),
    lng: String(lng),
    radiusMiles: String(radiusMiles),
  });
  return request<ZipBoundariesResponse>(`/api/zip-boundaries?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Stock imagery (design.md V7)
// ---------------------------------------------------------------------------

/** Which stock-image provider (if any) is live server-side. */
export function getImageConfig(): Promise<ImageConfigResponse> {
  return request<ImageConfigResponse>('/api/image-config');
}

/** Server-side Pexels search proxy — the key never reaches the client. */
export function searchStockImages(query: string): Promise<StockSearchResponse> {
  return request<StockSearchResponse>(`/api/stock-images?q=${encodeURIComponent(query)}`);
}

/** Downloads a stock (or pasted) image URL into this proposal's media store. */
export function saveProposalMedia(proposalId: string, url: string): Promise<MediaResponse> {
  return request<MediaResponse>(`/api/proposals/${encodeURIComponent(proposalId)}/media`, {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
}

// ---------------------------------------------------------------------------
// Share (read-only client link)
// ---------------------------------------------------------------------------

export function shareProposal(id: string): Promise<ShareResponse> {
  return request<ShareResponse>(`/api/proposals/${encodeURIComponent(id)}/share`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function getSharedProposal(token: string): Promise<SharedProposalResponse> {
  return request<SharedProposalResponse>(`/api/share/${encodeURIComponent(token)}`);
}

// ---------------------------------------------------------------------------
// View-only teammates (docs/viewer-sharing.md)
// ---------------------------------------------------------------------------

export function getProposalAccess(id: string): Promise<ProposalAccessResponse> {
  return request<ProposalAccessResponse>(`/api/proposals/${encodeURIComponent(id)}/access`);
}

export function getViewers(id: string): Promise<ViewerListResponse> {
  return request<ViewerListResponse>(`/api/proposals/${encodeURIComponent(id)}/viewers`);
}

export function setViewers(id: string, viewers: string[]): Promise<ViewerListResponse> {
  return request<ViewerListResponse>(`/api/proposals/${encodeURIComponent(id)}/viewers`, {
    method: 'PUT',
    body: JSON.stringify({ viewers }),
  });
}

// ---------------------------------------------------------------------------
// Chat (SSE)
// ---------------------------------------------------------------------------

/** Narrow an unknown parsed frame to a ChatEvent, dropping anything unknown. */
function asChatEvent(value: unknown): ChatEvent | null {
  if (!value || typeof value !== 'object') return null;
  const kind = (value as { kind?: unknown }).kind;
  if (
    kind === 'text' ||
    kind === 'tool' ||
    kind === 'status' ||
    kind === 'ask' ||
    kind === 'page_pending' ||
    kind === 'page_resolved' ||
    kind === 'decision' ||
    kind === 'state' ||
    kind === 'done' ||
    kind === 'error'
  ) {
    return value as ChatEvent;
  }
  return null;
}

/**
 * POST a chat turn and yield ChatEvents as they arrive.
 *
 * The transport is Server-Sent Events over a POST body, so EventSource can't be
 * used: we read the response stream and frame it by hand. Comment lines (`:`
 * heartbeats) and unknown fields are ignored; every `data:` payload is parsed
 * as one JSON ChatEvent.
 */
export async function* streamChat(
  id: string,
  body: ChatRequest,
  signal?: AbortSignal,
): AsyncGenerator<ChatEvent, void, undefined> {
  let res: Response;
  try {
    res = await fetch(`/api/proposals/${encodeURIComponent(id)}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(body),
      ...(signal ? { signal } : null),
    });
  } catch (cause) {
    yield { kind: 'error', message: `Could not reach the server (${String(cause)}).` };
    return;
  }

  if (!res.ok || !res.body) {
    if (res.status === 401) reportUnauthorized();
    const text = await res.text().catch(() => '');
    let message = `Chat request failed (${res.status}).`;
    try {
      const parsed: unknown = JSON.parse(text);
      if (parsed && typeof parsed === 'object' && 'error' in parsed) {
        message = String((parsed as { error: unknown }).error);
      }
    } catch {
      /* keep the status message */
    }
    yield { kind: 'error', message };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by a blank line.
      buffer = buffer.replace(/\r\n/g, '\n');
      let split = buffer.indexOf('\n\n');
      while (split !== -1) {
        const frame = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);
        const event = parseFrame(frame);
        if (event) yield event;
        split = buffer.indexOf('\n\n');
      }
    }
    const tail = parseFrame(buffer);
    if (tail) yield tail;
  } catch (cause) {
    if (signal?.aborted) return;
    yield { kind: 'error', message: `The stream ended unexpectedly (${String(cause)}).` };
  } finally {
    reader.cancel().catch(() => undefined);
  }
}

function parseFrame(frame: string): ChatEvent | null {
  const data: string[] = [];
  for (const rawLine of frame.split('\n')) {
    const line = rawLine.trimEnd();
    if (!line) continue;
    if (line.startsWith(':')) continue; // heartbeat / comment
    if (line.startsWith('data:')) {
      data.push(line.slice(5).replace(/^ /, ''));
    }
    // `event:`, `id:` and `retry:` carry nothing this protocol needs.
  }
  if (data.length === 0) return null;
  try {
    return asChatEvent(JSON.parse(data.join('\n')));
  } catch {
    return null;
  }
}
